import { LitElement, html, unsafeCSS } from "lit"
import { customElement, property } from "lit/decorators.js"
import type { HourRow } from "./types.js"
import gridStyles from "./styles/ephemeris-grid.css?inline"
import cellStyles from "./styles/ephemeris-row.css?inline"

@customElement("ephemeris-row")
export class EphemerisRow extends LitElement {
  @property({ type: Object }) row!: HourRow
  @property({ type: String }) celestialObjectName = "Object"
  @property({ type: String }) coordinateFormat: "hms" | "decimal" = "hms"
  @property({ type: Boolean }) isMoon = false
  @property({ type: String }) utcTime = ""
  @property({ type: String }) obsTime = ""
  @property({ type: Number }) obsDayOffset = 0
  @property({ type: String }) mineTime = ""
  @property({ type: Number }) mineDayOffset = 0

  static styles = [unsafeCSS(gridStyles), unsafeCSS(cellStyles)]

  protected render() {
    const { label: statusLabel, color: statusColor } = this.computeStatus()
    const statusClass = this.classForColor(statusColor)
    const elevClass = this.elevClass(this.row.target.elev)
    const moonSep = this.row.target.moonSeparation ?? 0
    const angleClass = this.angleClass(moonSep)
    const twilight = this.twilightInfo()
    const twilightClass = this.twilightClass(twilight.color)
    const { date } = this.splitDateTime(this.row.time)

    return html`
      <span role="cell">
        <span class=${`pill ${statusClass}`}>${statusLabel}</span>
      </span>
      <span role="cell">${date}</span>
      <span role="cell">${this.utcTime || "—"}</span>
      <span role="cell"
        >${this.obsTime || "—"}${this.obsDayOffset > 0
          ? html`<span>⏭️</span>`
          : this.obsDayOffset < 0
          ? html`<span>⏮️</span>`
          : ""}</span
      >
      <span role="cell"
        >${this.mineTime || "—"}${this.mineDayOffset > 0
          ? html`<span>⏭️</span>`
          : this.mineDayOffset < 0
          ? html`<span>⏮️</span>`
          : ""}</span
      >
      <span role="cell">
        <span class=${`pill ${twilightClass}`}>${twilight.label}</span>
      </span>
      <span role="cell">${this.row.target.azi.toFixed(2)}</span>
      <span role="cell">
        <span class=${`pill ${elevClass}`}>
          ${this.row.target.elev.toFixed(1)}°
        </span>
      </span>
      <span role="cell"
        >${this.coordinateFormat === "hms"
          ? this.row.target.ra
          : (this.row.target.raDeg / 15).toFixed(4) + "h"}</span
      >
      <span role="cell"
        >${this.coordinateFormat === "hms"
          ? this.row.target.dec
          : this.row.target.decDeg.toFixed(5) + "°"}</span
      >
      <span role="cell">
        <span class=${`pill ${angleClass}`}> ${moonSep.toFixed(1)}° </span>
      </span>
      <span role="cell" style="font-size: 1.5rem;">
        ${this.row.target.moonIllumination !== undefined
          ? this.getMoonPhaseIcon(this.row.target.moonIllumination)
          : "—"}
      </span>
    `
  }

  private computeStatus(): {
    label: "GO" | "WARN" | "NO"
    color: "green" | "yellow" | "orange" | "red"
  } {
    const elev = this.elevLight(this.row.target.elev)
    const moonSep = this.row.target.moonSeparation ?? 0
    const moonIllum = this.row.target.moonIllumination ?? 50
    const moon = this.moonLight(moonSep, moonIllum)
    const sun = this.sunLight()

    const colors = [elev.color, moon.color, sun.color]
    const yellowCount = colors.filter(c => c === "yellow").length

    const overallColor = colors.includes("red")
      ? "red"
      : yellowCount >= 2
      ? "orange"
      : colors.includes("yellow")
      ? "yellow"
      : "green"

    const label =
      overallColor === "green" ? "GO" : overallColor === "red" ? "NO" : "WARN"
    return { label, color: overallColor }
  }

  private elevClass(elev: number): string {
    const { color } = this.elevLight(elev)
    return color === "green"
      ? "elev-high"
      : color === "yellow"
      ? "elev-mid"
      : "elev-low"
  }

  private angleClass(angleDeg: number): string {
    const moonIllum = this.row.target.moonIllumination ?? 50
    const { color } = this.moonLight(angleDeg, moonIllum)
    return color === "green"
      ? "angle-high"
      : color === "yellow"
      ? "angle-mid"
      : "angle-low"
  }

  private splitDateTime(timeStr: string): { date: string; time: string } {
    const trimmed = timeStr.trim()
    const spaceIndex = trimmed.indexOf(" ")
    if (spaceIndex === -1) {
      return { date: trimmed, time: "" }
    }
    const date = trimmed.substring(0, spaceIndex)
    const time = trimmed.substring(spaceIndex + 1)
    return { date, time }
  }

  private elevLight(elev: number): { color: "green" | "yellow" | "red" } {
    // For Moon viewing, use lower thresholds since it's bright and observable at low altitudes
    if (this.isMoon) {
      if (elev >= 20) return { color: "green" }
      if (elev >= 10) return { color: "yellow" }
      return { color: "red" } // Only red if below 10° (too much atmospheric distortion)
    }

    // For other objects, maintain stricter thresholds
    if (elev >= 45) return { color: "green" }
    if (elev >= 30) return { color: "yellow" }
    return { color: "red" }
  }

  private moonLight(
    angleDeg: number,
    moonIllum: number
  ): { color: "green" | "yellow" | "red" } {
    // When viewing the Moon, separation is always 0 and always green
    if (this.isMoon) return { color: "green" }

    // Scale separation requirements based on Moon illumination percentage
    let greenThreshold: number
    let yellowThreshold: number

    if (moonIllum > 75) {
      // Full/Gibbous Moon - very bright, strictest requirements
      greenThreshold = 60
      yellowThreshold = 40
    } else if (moonIllum > 25) {
      // Quarter Moon - moderate brightness, moderate requirements
      greenThreshold = 45
      yellowThreshold = 30
    } else {
      // Crescent Moon - dim, relaxed requirements
      greenThreshold = 30
      yellowThreshold = 20
    }

    if (angleDeg >= greenThreshold) return { color: "green" }
    if (angleDeg >= yellowThreshold) return { color: "yellow" }
    return { color: "red" }
  }

  private sunLight(): { color: "green" | "yellow" | "red" } {
    const twilight = this.row.target.twilight ?? ""

    // When viewing the Moon, it's bright enough to observe during twilight and even daytime
    if (this.isMoon) {
      // Moon is observable even in civil twilight
      if (
        twilight === "" ||
        twilight === " " ||
        twilight === "A" ||
        twilight === "N"
      ) {
        return { color: "green" }
      }
      if (twilight === "C") return { color: "yellow" }
      return { color: "red" } // Only fail in full daylight (*)
    }

    // For other objects, require darker skies
    // Markers: '*' = day, 'C' = civil, 'N' = nautical, 'A' = astronomical, ' ' = night
    switch (twilight) {
      case "":
      case " ":
      case "A":
        return { color: "green" } // Night or astronomical twilight
      case "N":
        return { color: "yellow" } // Nautical twilight
      default:
        return { color: "red" } // Civil twilight or day
    }
  }

  private twilightInfo(): {
    label: string
    color: "green" | "yellow" | "red"
  } {
    const marker = this.row.target.twilight ?? ""

    // Horizons markers: '*' = day, 'C' = civil, 'N' = nautical, 'A' = astronomical, ' ' = night
    switch (marker) {
      case "*":
        return { label: "Day", color: "red" }
      case "C":
        return { label: "Civil", color: "red" }
      case "N":
        return { label: "Nautical", color: "yellow" }
      case "A":
        return { label: "Astro", color: "green" }
      default:
        return { label: "Night", color: "green" }
    }
  }

  private twilightClass(color: "green" | "yellow" | "red") {
    if (color === "green") return "twilight-night"
    if (color === "yellow") return "twilight-nautical"
    return "twilight-day"
  }

  private classForColor(color: "green" | "yellow" | "orange" | "red") {
    if (color === "green") return "status-go"
    if (color === "yellow") return "status-caution"
    if (color === "orange") return "status-orange"
    return "status-no"
  }

  private getMoonPhaseIcon(illumination: number): string {
    // Return emoji based on Moon illumination percentage
    if (illumination < 1) return "🌑" // New Moon
    if (illumination < 12.5) return "🌒" // Waxing Crescent
    if (illumination < 37.5) return "🌓" // First Quarter
    if (illumination < 62.5) return "🌔" // Waxing Gibbous
    if (illumination < 87.5) return "🌕" // Full Moon
    if (illumination < 99) return "🌖" // Waning Gibbous
    return "🌗" // Last Quarter / Waning Crescent
  }
}
